import '@/assets/styles/global.scss'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import App from './App.vue'
import router from './router'
import { setupDirectives } from './directives'
import { setupEventTracker } from '@/plugins/event-tracker'
import { createVersionPolling } from 'version-polling'
import { preloadFonts } from './utils/font-preload'
import { isApp, tcGetStatusBarHeight } from './utils/bridge'
import { setupAppPageTracking } from './plugins/app-page-tracking'
import { initSentry } from './utils/sentry'
import { getApiBaseUrl } from './utils/env'
import { useAppStore } from '@/stores/app'
import { applyKeepAliveConfig, applyStatusBarHeight } from '@/utils/keep-alive-config'
import { setupPcPointerCompatibility } from '@/utils/pc-pointer-compat'
import { prepareAiLoginEntry, scrubAiLoginApiKeyFromAddress } from '@/utils/aiLoginEntry'

prepareAiLoginEntry()
setupPcPointerCompatibility()
preloadFonts()

// 启用 vconsole 和打印日志
if (import.meta.env.VITE_APP_ENV === 'dev' || import.meta.env.VITE_ENABLE_VCONSOLE === 'true') {
  import('vconsole').then((module) => {
    new module.default()
  })
  console.log('当前环境:', import.meta.env.VITE_APP_ENV)
  console.log('API地址:', getApiBaseUrl())
}

async function bootstrap() {
  const app = createApp(App)

  // 创建 Pinia 实例并注册持久化插件
  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  app.use(pinia)

  // App 环境：初始化设备信息（在路由和组件挂载之前）
  if (isApp()) {
    const appStore = useAppStore()
    await appStore.initAllAppData()
    // 根据 App 版本号决定是否启用 Tab 保活（低版本兜底关闭）
    applyKeepAliveConfig(appStore.enableNewHeader)
    // 获取并注入状态栏高度，供 NavHeader 顶部留位
    try {
      const statusBarHeight = await tcGetStatusBarHeight()
      applyStatusBarHeight(statusBarHeight)
    } catch (error) {
      console.error('[main] 获取状态栏高度失败:', error)
    }
  } else {
    // H5 环境直接启用保活
    applyKeepAliveConfig(true)
  }

  app.use(router)

  // 路由守卫会在首次导航期间上报 Dongcha 进入事件，必须先启动 SDK，避免首个事件被丢弃。
  setupEventTracker()
  await router.isReady()
  scrubAiLoginApiKeyFromAddress()
  // 注册全局指令
  setupDirectives(app)

  // 初始化 Sentry（必须在 router 之后，app.mount 之前）
  initSentry(app, router)

  // App 环境：初始化页面埋点（每个页面是独立 WebView）
  if (isApp()) {
    setupAppPageTracking()
    console.log('[App] 已启用 App 页面埋点')
  }

  // 全局错误监听：处理动态模块加载失败（通常发生在版本更新后）
  if (['uat', 'prod'].includes(import.meta.env.VITE_APP_ENV)) {
    createVersionPolling({
      silent: false,
      vcType: 'chunkHash',
      chunkName: 'index',
      silentPollingInterval: true,
      onUpdate: (self) => {
        console.warn('检测到网页有更新, 刷新页面')
        self.onRefresh()
      },
    })
  }
  window.addEventListener('vite:preloadError', () => {
    window.location.reload()
  })

  app.mount('#app')
}

bootstrap()
